import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import GridBoard from '../GridBoard';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import { useEffect, useState } from 'react';
import { hexGridAspectRatio } from '../../utils/gridConfig';
import { getSystemById } from '../../data/ttrpgSystems';
import { ArrowUpFromLine, CircleOff, Trash2 } from 'lucide-react';

const CONFIRM_CLICKS = 3;

function ConfirmButton({ icon, onConfirm }) {
  const [clicksLeft, setClicksLeft] = useState(CONFIRM_CLICKS);

  const handleClick = () => {
    if (clicksLeft <= 1) {
      setClicksLeft(CONFIRM_CLICKS);
      onConfirm();
      return;
    }
    setClicksLeft((previous) => previous - 1);
  };

  return (
    <Button
      label={"Delete"}
      icon={icon}
      description={
        clicksLeft < CONFIRM_CLICKS
          ? `Press ${clicksLeft} more time${clicksLeft !== 1 ? 's' : ''} to Delete`
          : ''
      }
      type='cinnabar'
      onPress={handleClick}
    />
  );
}

function DeleteSceneModal({
  visible,
  scene,
  gridConfig,
  onCancel,
  onConfirm,
  onExport,
  mode = 'light',
}) {
  const isDark = mode === 'dark';
  const [isPhone, setIsPhone] = useState(false);
  const gridCols = gridConfig?.cols || 24;
  const gridRows = gridConfig?.rows || 30;
  const gridAspectRatio = gridConfig?.type === 'hex'
    ? hexGridAspectRatio(gridCols, gridRows)
    : gridCols / gridRows;
  const previewHeight = 180;
  const previewScene = scene
    ? { ...scene, tokens: [], battleEffects: [], effect: null }
    : null;
  const systemName = getSystemById(scene?.ttrpgSystem)?.shortName || scene?.ttrpgSystem || 'Not specified';
  const sceneBreakdown = [
    ['System', systemName],
    ['Tokens', scene?.tokens?.length || 0],
    ['Battle Effects', scene?.battleEffects?.length || 0],
    ['Layers', scene?.layers?.length || 0],
    ['Orchestrations', 0],
  ];

  useEffect(() => {
    const mediaQueryWidth = window.matchMedia('(max-width: 620px)');
    const handleMediaChange = () => {
      setIsPhone(mediaQueryWidth.matches);
    };
    handleMediaChange();
    mediaQueryWidth.addListener(handleMediaChange);
    return () => {
      mediaQueryWidth.removeListener(handleMediaChange);
    };
  }, []);

  return (
    <Rodal
      animation='slideUp'
      visible={visible}
      onClose={onCancel}
      className={isDark ? 'df_rodal_dark' : ''}
      customStyles={{
        border: isDark ? '2px solid var(--onyx)' : '2px solid #e4E7C5',
        backgroundColor: isDark ? 'var(--jet)' : '#f6f7eb',
        color: isDark ? 'var(--ivory)' : 'var(--jet)',
        width: isPhone ? 'calc(100% - 34px)' : 450,
        height: isPhone ? 'calc(100% - 114px)' : 518,
        marginTop: isPhone ? '80px' : '',
      }}
    >
      <div
        className={isDark ? 'df_modal_form_dark' : ''}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div
            className='trialment_login_form_row'
            style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, textAlign: 'center' }}
          >
            Delete {scene?.name || 'this scene'}?
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '4px 16px',
              fontSize: '12px',
              fontWeight: 700,
              marginBottom: 8,
            }}
          >
            {sceneBreakdown.map(([label, count]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{label}:</span>
                <span>{count}</span>
              </div>
            ))}
          </div>
          <div
            style={{
              height: `${previewHeight}px`,
              width: `${previewHeight * gridAspectRatio}px`,
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              marginBottom: 8,
              pointerEvents: 'none',
            }}
          >
            {previewScene && (
              <GridBoard
                scene={previewScene}
                readOnly
                gridCols={gridCols}
                gridRows={gridRows}
                gridType={gridConfig?.type || 'square'}
              />
            )}
          </div>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 700,
              color: '#e94f37',
              lineHeight: 1.45,
              marginBottom: 8,
            }}
          >
            WARNING: Deleting an Orchestrator scene CAN&apos;T BE UNDONE! Deleted Scenes are NON-RECOVERABLE, EVEN FROM THE CLOUD! Please export the scene first for good measure! Deleting a Scene while players are actively using it via Gameplay Sync is not advised. If you do it anyway and face issues, refresh Orchestrator and give your players a new Session Token!
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '8px',
            marginBottom: '8px',
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          <Button
            label='Export Scene'
            icon={ArrowUpFromLine}
            type={isDark ? 'onyx' : 'black-white'}
            onPress={onExport}
          />
          <Button
            label='Cancel'
            icon={CircleOff}
            type={mode === 'dark' ? 'onyx' : 'black-white'}
            onPress={onCancel}
          />
          <ConfirmButton onConfirm={onConfirm} icon={Trash2} />
        </div>
      </div>
    </Rodal>
  );
}

export default DeleteSceneModal;
